import Breadcrumb from "components/Common/Breadcrumb";
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, Col, Label, Row, Input } from "reactstrap";
import moment from "moment-timezone";
import { AdminService } from "services/AdminService";
import { toast } from "react-toastify";
import { Puff } from "react-loader-spinner";
import DriverReportTableContainer from "./DriverReportTableContainer";
import DriverReportView from "./DriverReportView";

toast.configure();

interface DriverSummary {
  _id: string;
  driverId: string;
  customerId: string;
  driverName: string;
  percentage: number;
  weekData: {
    weekNumber: number;
    year: number;
    totalCommission: number;
    totalOrderValue: number;
    orderCount: number;
    totalActiveSeconds: number;
    orderDetails: any[];
    updatedAt: string;
  };
  totalOrders: number;
  totalOrderValue: number;
  totalCommission: number;
}

const OldReports: React.FC = () => {
  const [allDrivers, setAllDrivers] = useState<DriverSummary[]>([]);
  const [drivers, setDrivers] = useState<DriverSummary[]>([]);
  const [selectedDriver, setSelectedDriver] = useState<any>(null);
  const [modal, setModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const [LIMIT_P_C] = useState(10);
  const [isOffset_p_c, setOffset_p_c] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const [selectedWeek, setSelectedWeek] = useState<string>("");
  const [selectedYear, setSelectedYear] = useState<number>(moment().year());
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [availableWeeks, setAvailableWeeks] = useState<{ value: string; label: string }[]>([]);


  const getSelectedWeekDates = () => {
    if (!selectedWeek) {
      const now = moment.tz("America/Toronto");
      return {
        startOfWeek: now.clone().startOf("isoWeek"),
        endOfWeek: now.clone().endOf("isoWeek")
      };
    }

    const [year, weekNumber] = selectedWeek.split('-').map(Number);
    const startOfWeek = moment().year(year).isoWeek(weekNumber).startOf('isoWeek');
    const endOfWeek = moment().year(year).isoWeek(weekNumber).endOf('isoWeek');

    return { startOfWeek, endOfWeek };
  };

  const { startOfWeek, endOfWeek } = getSelectedWeekDates();


  const generateYears = () => {
    const currentYear = moment().year();
    return [currentYear - 2, currentYear - 1, currentYear];
  };


  const generateWeekOptions = (year: number) => {
    const weeks: { value: string; label: string }[] = [];
    const startOfYear = moment().year(year).startOf('year');
    const endOfYear = moment().year(year).endOf('year');
    const now = moment().tz("America/Toronto");

    let currentWeek = startOfYear.clone().startOf('isoWeek');

    while (currentWeek.isBefore(endOfYear)) {
      const weekNumber = currentWeek.isoWeek();
      const startDate = currentWeek.clone().startOf('isoWeek');
      const endDate = currentWeek.clone().endOf('isoWeek');

      if (endDate.isSameOrBefore(now, 'day')) {
        weeks.push({
          value: `${year}-${weekNumber}`,
          label: `Week ${weekNumber} (${startDate.format('MMM D')} - ${endDate.format('MMM D')})`
        });
      }

      currentWeek = currentWeek.add(1, 'week');
    }

    return weeks;
  };


  const getLatestAvailableWeek = (weeks: { value: string; label: string }[]) => {
    if (weeks.length === 0) return "";

    const sortedWeeks = [...weeks].sort((a, b) => {
      const [yearA, weekA] = a.value.split('-').map(Number);
      const [yearB, weekB] = b.value.split('-').map(Number);

      if (yearA !== yearB) return yearB - yearA;
      return weekB - weekA;
    });

    return sortedWeeks[0]?.value || "";
  };

  useEffect(() => {
    setIsLoading(true);
    const years = generateYears();
    setAvailableYears(years);
    setSelectedYear(years[years.length - 1]);
    fetchLatestWeekData();
  }, []);


  const fetchLatestWeekData = async () => {
    setIsLoading(true);
    try {
      const response = await AdminService.getOldWeekDriversSummary();
      if (response.success) {
        const data = response.data || [];
        setAllDrivers(data);
        setDrivers(data);
        setPageCount(Math.ceil(data.length / LIMIT_P_C));

        if (data.length > 0 && data[0].weekData) {
          const weekData = data[0].weekData;
          const year = weekData.year;
          const weekNumber = weekData.weekNumber;

          setSelectedYear(year);
          const weeks = generateWeekOptions(year);
          setAvailableWeeks(weeks);

          const weekValue = `${year}-${weekNumber}`;
          const weekExists = weeks.some(w => w.value === weekValue);

          if (weekExists) {
            setSelectedWeek(weekValue);
          } else {
            const latestWeek = getLatestAvailableWeek(weeks);
            setSelectedWeek(latestWeek);
          }
        }
      } else {
        toast.error("Failed to load driver reports");
      }
    } catch (error) {
      toast.error("Failed to load driver reports");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const weeks = generateWeekOptions(selectedYear);
    setAvailableWeeks(weeks);

    const latestWeek = getLatestAvailableWeek(weeks);
    if (latestWeek && selectedWeek !== latestWeek) {
      setSelectedWeek(latestWeek);
    } else if (!latestWeek && weeks.length > 0) {
      setSelectedWeek(weeks[weeks.length - 1]?.value || "");
    }
  }, [selectedYear]);


  const fetchWeekData = async (year: number, week: number) => {
    setIsLoading(true);
    try {
      const response = await AdminService.getOldWeekDriversSummary(year, week);
      if (response.success) {
        const data = response.data || [];
        setAllDrivers(data);
        setDrivers(data);
        setPageCount(Math.ceil(data.length / LIMIT_P_C));
      } else {
        toast.error("Failed to load driver reports");
      }
    } catch (error) {
      toast.error("Failed to load driver reports");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedWeek) {
      const [year, week] = selectedWeek.split('-').map(Number);
      fetchWeekData(year, week);
    }
  }, [selectedWeek]);


  const handleSendEmailSimple = async (selectedIds: string[], selectedOtherIds: string[]) => {

    const emailPromises = selectedIds.map(async (commissionId, index) => {
      const driverId = selectedOtherIds[index];

      if (!commissionId || !driverId) {
        return null;
      }

      const emailData = {
        commisionIds: [commissionId],
        driverId: driverId
      };

      try {
        const res = await AdminService.sendDriverReportEmail(emailData);
        return { commissionId, driverId, success: res.success, error: res.error };
      } catch (error) {
        return { commissionId, driverId, success: false, error: "Network error" };
      }
    });

    setIsSendingEmail(true);

    try {
      const results = await Promise.all(emailPromises);
      // const validResults = results.filter(result => result !== null);
      const validResults = results.filter(
        (result): result is NonNullable<typeof result> => result !== null);


      // const successful = validResults.filter(result => result.success);
      // const failed = validResults.filter(result => !result.success);

      const successful = validResults.filter(result => result.success);
      const failed = validResults.filter(result => !result.success);


      if (successful.length > 0) {
        toast.success(`Report emails sent successfully for ${successful.length} item(s)`, {
          position: toast.POSITION.BOTTOM_RIGHT,
          className: "foo-bar",
          style: { marginBottom: "4rem" },
        });
      }

      if (failed.length > 0) {
        toast.error(`Failed to send ${failed.length} email(s)`, {
          position: toast.POSITION.BOTTOM_RIGHT,
          className: "foo-bar",
          style: { marginBottom: "4rem" },
        });
      }

    } catch (error) {
      console.error("Email sending error:", error);
      toast.error("Failed to send emails. Please try again.", {
        position: toast.POSITION.BOTTOM_RIGHT,
        className: "foo-bar",
        style: { marginBottom: "4rem" },
      });
    } finally {
      setIsSendingEmail(false);
    }
  };


  const SeeMore = (page: any) => setOffset_p_c(page.selected + 1);


  const searchTable = (text: string) => {
    if (!text) {
      setDrivers(allDrivers);
      setPageCount(Math.ceil(allDrivers.length / LIMIT_P_C));
      setOffset_p_c(1);
      return;
    }

    const filtered = allDrivers.filter(
      (d) =>
        d.driverName.toLowerCase().includes(text.toLowerCase()) ||
        d.customerId.toLowerCase().includes(text.toLowerCase())
    );

    setDrivers(filtered);
    setPageCount(Math.ceil(filtered.length / LIMIT_P_C));
    setOffset_p_c(1);
  };


  const handleViewInvoice = async (row: any) => {
    const driver = row.original;

    // Fetch driver details to get email
    let driverEmail = "";
    let driverFirstName = driver.driverName;
    let driverLastName = "";

    try {
      const res = await AdminService.getDriverById(driver.driverId || driver._id);
      if (res.success && res.data) {
        driverEmail = res.data.email || "";
        driverFirstName = res.data.firstName || driver.driverName;
        driverLastName = res.data.lastName || "";
      }
    } catch (error) {
      console.error("Failed to fetch driver details:", error);
    }

    const invoicePayload = {
      _id: driver._id,
      zuluId: `INV-${driver.weekData.year}-W${driver.weekData.weekNumber
        .toString()
        .padStart(2, "0")}-${driver.customerId || "N/A"}`,
      totalOrders: driver.totalOrders,
      totalOrderAmount: driver.totalOrderValue,
      totalCommision: driver.totalCommission,
      orderDetails: driver.weekData?.orderDetails || [],
      driverDetails: {
        customerId: driver.customerId,
        firstName: driverFirstName,
        lastName: driverLastName,
        email: driverEmail,
        percentage: driver.percentage,
        _id: driver.driverId || driver._id,
      },
      previousWeek: null
    };

    setSelectedDriver(invoicePayload);
    setModal(true);
  };


  const formatActiveHours = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  };


  const columns = useMemo(
    () => [
      {
        id: "customerId",
        Header: "Driver ID",
        accessor: "customerId",
      },
      {
        id: "driverName",
        Header: "Driver Name",
        accessor: "driverName",
      },
      {
        id: "percentage",
        Header: "Commission Rate",
        accessor: "percentage",
        Cell: ({ value }: any) => `${value}%`,
      },
      {
        id: "orders",
        Header: "Orders",
        accessor: "totalOrders",
      },
      {
        id: "value",
        Header: "Order Value",
        accessor: "totalOrderValue",
        Cell: ({ value }: any) => `$${(value || 0).toFixed(2)}`,
      },
      {
        id: "commission",
        Header: "Commission",
        accessor: "totalCommission",
        Cell: ({ value }: any) => `$${(value || 0).toFixed(2)}`,
      },
      {
        id: "activeHours",
        Header: "Active Hours",
        accessor: "weekData.totalActiveSeconds",
        Cell: ({ value }: any) => formatActiveHours(value || 0),
      },
      {
        id: "action",
        Header: "Action",
        Cell: ({ row }: any) => (
          <div className="d-flex justify-content-center">
            <button
              className="btn btn-primary btn-sm px-3"
              onClick={() => handleViewInvoice(row)}
            >
              View Invoice
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const paginatedData = useMemo(() => {
    const start = (isOffset_p_c - 1) * LIMIT_P_C;
    return drivers.slice(start, start + LIMIT_P_C);
  }, [drivers, isOffset_p_c, LIMIT_P_C]);

  const selectedWeekData = availableWeeks.find(w => w.value === selectedWeek);

  return (
    <React.Fragment>
      <div className="page-content mb-5">
        <div className="container-fluid">
          <Breadcrumb title="Driver Weekly Reports" breadcrumbItem="Summary" />


          <Row className="mb-4">
            <Col lg="12">
              <Card>
                <CardBody>
                  <h5 className="card-title mb-3">Filter by Week</h5>
                  <Row>
                    <Col md="4">
                      <Label htmlFor="year-select">Select Year</Label>
                      <Input
                        type="select"
                        id="year-select"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                      >
                        {availableYears.map(year => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md="8">
                      <Label htmlFor="week-select">Select Week</Label>
                      <Input
                        type="select"
                        id="week-select"
                        value={selectedWeek}
                        onChange={(e) => setSelectedWeek(e.target.value)}
                      >
                        <option value="">Select a week</option>
                        {availableWeeks.map(week => (
                          <option key={week.value} value={week.value}>
                            {week.label}
                          </option>
                        ))}
                      </Input>
                    </Col>
                  </Row>
                  {selectedWeek && (
                    <div className="mt-3 p-2 bg-light rounded">
                      <strong>Selected Period:</strong> {
                        selectedWeekData?.label || `Week ${selectedWeek.split('-')[1]}, ${selectedWeek.split('-')[0]}`
                      }
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>


          {!isLoading && drivers.length > 0 && (
            <Row className="mb-4">
              <Col lg="12">
                <div className="p-3 bg-light rounded d-flex justify-content-between flex-wrap">
                  <div><strong>Total Drivers:</strong> {drivers.length}</div>
                  <div><strong>Total Orders:</strong> {drivers.reduce((sum, driver) => sum + (driver.totalOrders || 0), 0)}</div>
                  <div><strong>Total Commission:</strong> ${drivers.reduce((sum, driver) => sum + (driver.totalCommission || 0), 0).toFixed(2)}</div>
                  <div><strong>Total Order Value:</strong> ${drivers.reduce((sum, driver) => sum + (driver.totalOrderValue || 0), 0).toFixed(2)}</div>
                </div>
              </Col>
            </Row>
          )}


          <Row>
            <Col lg="12">
              <Card className="mb-5">
                <CardBody>
                  {isLoading ? (
                    <div className="d-flex justify-content-center" style={{ height: 400 }}>
                      <Puff height={60} width={60} />
                    </div>
                  ) : (
                    <DriverReportTableContainer
                      columns={columns}
                      data={paginatedData}
                      pagelimit={pageCount}
                      handlePageChange={SeeMore}
                      handleSearch={searchTable}
                      customPageSize={50}
                      className="custom-header-css"
                      hasCheckBox={true}
                      handleSendEmail={handleSendEmailSimple}
                      isSendingEmail={isSendingEmail}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>


          <DriverReportView
            isOpen={modal}
            handlePopup={() => setModal(false)}
            driverBill={selectedDriver}
            dates={{ date1: startOfWeek, date2: endOfWeek }}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default OldReports;